import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { urlArchivo, abrirArchivoSeguro } from "../utils/upload";

// Miniatura del archivo de referencia de un producto. Los archivos son privados,
// así que se piden con la sesión iniciada. Una imagen se amplía al tocarla;
// un PDF se abre en otra pestaña.
// vistaPrevia: imagen local (antes de guardar la solicitud), para no pedirla al servidor.

// Cada archivo se descarga una sola vez por visita: al ocultar y volver a mostrar la
// lista se reutiliza. Si falla, se borra para poder intentarlo de nuevo.
const archivosDescargados = new Map();
const ESPERA_MAXIMA_MS = 20000;

function descargarUnaVez(nombreArchivo) {
  if (!archivosDescargados.has(nombreArchivo)) {
    const token = localStorage.getItem("token");
    const control = new AbortController();
    const limite = setTimeout(() => control.abort(), ESPERA_MAXIMA_MS);
    const promesa = fetch(urlArchivo(nombreArchivo), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: control.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.blob();
      })
      .then((blob) => (blob.type.startsWith("image/") ? { imagen: URL.createObjectURL(blob) } : { esPdf: true }))
      .catch((err) => {
        archivosDescargados.delete(nombreArchivo);
        throw err;
      })
      .finally(() => clearTimeout(limite));
    archivosDescargados.set(nombreArchivo, promesa);
  }
  return archivosDescargados.get(nombreArchivo);
}

function MiniaturaArchivo({ nombreArchivo, vistaPrevia }) {
  const [imagen, setImagen] = useState(vistaPrevia || null);
  const [esPdf, setEsPdf] = useState(false);
  const [error, setError] = useState(false);
  const [ampliada, setAmpliada] = useState(false);

  useEffect(() => {
    if (vistaPrevia) {
      setImagen(vistaPrevia);
      return undefined;
    }
    let cancelado = false;
    descargarUnaVez(nombreArchivo)
      .then((resultado) => {
        if (cancelado) return;
        if (resultado.imagen) setImagen(resultado.imagen);
        else setEsPdf(true);
      })
      .catch(() => !cancelado && setError(true));
    return () => {
      cancelado = true;
    };
  }, [nombreArchivo, vistaPrevia]);

  useEffect(() => {
    if (!ampliada) return undefined;
    const cerrarConEsc = (e) => e.key === "Escape" && setAmpliada(false);
    window.addEventListener("keydown", cerrarConEsc);
    return () => window.removeEventListener("keydown", cerrarConEsc);
  }, [ampliada]);

  const detener = (e) => e.stopPropagation();

  if (error) {
    return (
      <span style={estilos.chip} title="No se pudo cargar el archivo adjunto">
        📎 Sin vista previa
      </span>
    );
  }

  if (esPdf) {
    return (
      <button
        type="button"
        style={estilos.chipBoton}
        onClick={(e) => {
          detener(e);
          abrirArchivoSeguro(e, nombreArchivo);
        }}
        title="Abrir el PDF adjunto"
      >
        📄 Ver PDF
      </button>
    );
  }

  if (!imagen) return <span style={estilos.cargando} aria-label="Cargando imagen" />;

  return (
    <>
      <button
        type="button"
        style={estilos.miniatura}
        onClick={(e) => {
          detener(e);
          setAmpliada(true);
        }}
        title="Ver la imagen adjunta"
      >
        <img src={imagen} alt="Imagen de referencia del producto" style={estilos.imagen} />
      </button>
      {ampliada &&
        createPortal(
        <div
          style={estilos.fondo}
          onClick={(e) => {
            detener(e);
            setAmpliada(false);
          }}
          role="dialog"
          aria-label="Imagen de referencia"
        >
          <img src={imagen} alt="Imagen de referencia del producto" style={estilos.grande} onClick={detener} />
          <button type="button" style={estilos.cerrar} onClick={() => setAmpliada(false)} aria-label="Cerrar">
            ✕
          </button>
        </div>,
          document.body
        )}
    </>
  );
}

const estilos = {
  miniatura: {
    flexShrink: 0,
    width: 46,
    height: 46,
    padding: 0,
    border: "2px solid var(--tema-linea, #d3d9df)",
    borderRadius: 8,
    overflow: "hidden",
    background: "#fff",
    cursor: "zoom-in",
  },
  imagen: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  cargando: {
    flexShrink: 0,
    display: "inline-block",
    width: 46,
    height: 46,
    borderRadius: 8,
    background: "#eef1f5",
  },
  chip: {
    flexShrink: 0,
    fontSize: "0.75rem",
    color: "#8993a1",
    whiteSpace: "nowrap",
  },
  chipBoton: {
    flexShrink: 0,
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "var(--tema-titulo, #0b3c60)",
    background: "var(--tema-suave, #eef3fa)",
    border: "1px solid var(--tema-linea, #d3d9df)",
    borderRadius: 999,
    padding: "0.2rem 0.6rem",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  fondo: {
    position: "fixed",
    inset: 0,
    zIndex: 2000,
    background: "rgba(10, 25, 40, 0.82)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
    cursor: "zoom-out",
  },
  grande: {
    maxWidth: "100%",
    maxHeight: "90vh",
    borderRadius: 10,
    boxShadow: "0 10px 40px rgba(0,0,0,0.45)",
    background: "#fff",
    cursor: "default",
  },
  cerrar: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 40,
    height: 40,
    borderRadius: "50%",
    border: "none",
    background: "#fff",
    color: "#0b3c60",
    fontSize: "1.1rem",
    fontWeight: 700,
    cursor: "pointer",
  },
};

export default MiniaturaArchivo;
